/* F2 — client view of the server-owned pickup eligibility answer (§11.3).
 * The answer itself is authoritative for RENDERING only: it is never a
 * reservation and never proof of acceptance — validate/submit re-run the
 * same canonical policy server-side. These helpers are pure so the chip
 * strip, the submit gate and their tests all share one interpretation. */

import type { PickupShopEligibility } from '../api/types';
import { civilDatesBetween, civilWeekday } from './dates';

/** Safety skew around the documented validUntil bound — an answer is
 * treated as stale slightly before expiry so the UI never rides the edge. */
export const ELIGIBILITY_SKEW_MS = 2_000;

/** Max chips ever rendered — the contract's configured horizon cap (365)
 * plus slack; the strip stays bounded even against a malformed range. */
export const ELIGIBILITY_MAX_DAYS = 370;

/** F2.1 — stale-refresh cycle budget: when an eligibility answer arrives
 * already stale the page may auto-retry at most twice (~400 ms apart)
 * before settling on actionable feedback. The budget is consumed where
 * the answer is classified (fetchEligibility) and keyed by (owner, shop),
 * so `loading` transitions inside one cycle never reset it; a fresh
 * `ready` answer, a context change, or an explicit user retry ends or
 * starts a cycle. */
export const ELIGIBILITY_STALE_MAX_RETRIES = 2;
export const ELIGIBILITY_STALE_RETRY_MS = 400;

/** True once the documented freshness bound has passed (or is unparseable —
 * a broken timestamp must never be trusted as fresh). */
export function eligibilityStale(data: PickupShopEligibility, now = Date.now()): boolean {
  const until = Date.parse(data.validUntil);
  return !Number.isFinite(until) || now >= until - ELIGIBILITY_SKEW_MS;
}

/** Inclusive window date keys for the strip — empty when the shop is
 * unavailable (nothing may render from an unavailable answer). */
export function eligibilityDateKeys(data: PickupShopEligibility): string[] {
  if (!data.available) return [];
  return civilDatesBetween(data.minDate, data.maxDate, ELIGIBILITY_MAX_DAYS);
}

/** One date key's selectability: inside the inclusive window AND not a
 * published closed weekday AND a real calendar date (civilWeekday nulls on
 * malformed keys — never selectable). */
export function isDateEligible(key: string, data: PickupShopEligibility): boolean {
  if (!data.available) return false;
  if (key < data.minDate || key > data.maxDate) return false;
  const wd = civilWeekday(key);
  return wd !== null && !data.closedWeekdays.includes(wd);
}
